import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams, useLocation } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Modal } from 'components/ui/Modal'
import { useTheme } from 'contexts/ThemeContext'
import { useAuth } from 'contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { useToast } from 'contexts/ToastContext'
import { exportToCSV } from 'lib/utils'
import axios from 'axios'
import { Switch } from 'components/ui/switch'
import { PageHeader } from 'components/layout/PageHeader'
import api from 'lib/api'
import {
  candidatesApi,
  offersApi,
  emailConfigApi,
  authApi,
  preferencesApi,
  aiAssistantApi,
  rolesApi,
  dataExportApi,
  applicationsApi,
  type Candidate,
  type Offer,
  type EmailConfiguration,
  type User,
  type UserRole,
} from 'lib/api'
import { NotificationPreferences } from '../components/NotificationPreferences'
import { OutgoingEmailLogTable } from '../components/OutgoingEmailLogTable'
import {
  Palette,
  Download,
  Bell,
  Shield,
  Database,
  Sun,
  Moon,
  Settings as SettingsIcon,
  User as UserIcon,
  Mail,
  Phone,
  MapPin,
  Save,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Brain,
  BarChart3,
  RefreshCw,
  Upload,
  FileText,
  Calendar,
  Clock,
  Send,
  Server,
  Key,
  TestTube,
  Edit,
  AlertCircle
} from 'lucide-react'

import {
  readCachedManualMatchingTimeMinutes,
  updateManualMatchingTimeInDashboardCaches,
} from 'features/dashboard/utils/dashboardStatsCache'

// Session timeout options in minutes: 4h, 8h, 1 day, 2 days, 1 week
const SESSION_TIMEOUT_OPTIONS = [240, 480, 1440, 2880, 10080]

function resolveInitialManualMatchingTimeMinutes(locationState: unknown): number | null {
  const fromNavigation = (locationState as { manualMatchingTimeMinutes?: number } | null)?.manualMatchingTimeMinutes
  if (typeof fromNavigation === 'number' && fromNavigation > 0) {
    return fromNavigation
  }

  return readCachedManualMatchingTimeMinutes()
}

export function Settings() {
  const { theme, toggleTheme } = useTheme()
  const { user, hasPermission } = useAuth()
  const { t } = useTranslation()
  const { addToast } = useToast()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const initialManualMatchingTimeMinutes = resolveInitialManualMatchingTimeMinutes(location.state)

  // State management
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'profile')
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [isOrgAdmin, setIsOrgAdmin] = useState(false)
  const [isLoadingRoles, setIsLoadingRoles] = useState(true)

  // User profile state
  const [profile, setProfile] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    username: user?.username || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
    // calendly_link: (user as any)?.calendly_link || '' // Calendly integration disabled
  })
  const [currentUser, setCurrentUser] = useState<User | null>(null)

  // Notification preferences
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    weeklyReports: false,
    matchingUpdates: true,
    candidateUpdates: true,
    offerUpdates: true
  })

  // AI Matching preferences
  const [aiSettings, setAiSettings] = useState({
    autoMatching: false,
    matchingThreshold: 70,
    skillWeight: 40,
    experienceWeight: 30,
    locationWeight: 20,
    educationWeight: 10
  })

  // Session timeout (minutes)
  const [sessionTimeout, setSessionTimeout] = useState<number>(240)
  const [isSavingSessionTimeout, setIsSavingSessionTimeout] = useState(false)

  // Dashboard matching settings
  const [manualMatchingTimeMinutes, setManualMatchingTimeMinutes] = useState<number | null>(
    initialManualMatchingTimeMinutes
  )
  const [isLoadingDashboardSettings, setIsLoadingDashboardSettings] = useState(
    initialManualMatchingTimeMinutes === null
  )
  const [isSavingDashboardSettings, setIsSavingDashboardSettings] = useState(false)
  const dashboardSettingsLoadedRef = useRef(false)

  // Data management
  const [dataStats, setDataStats] = useState({
    candidates: 0,
    offers: 0,
    applications: 0,
    lastBackup: null as string | null
  })

  // Export states for data management
  const [exportStates, setExportStates] = useState<{
    [key: string]: { loading: boolean; success: boolean; error: string | null }
  }>({
    candidates: { loading: false, success: false, error: null },
    offers: { loading: false, success: false, error: null },
    users: { loading: false, success: false, error: null },
    matchings: { loading: false, success: false, error: null },
    hiring_process: { loading: false, success: false, error: null },
    backup: { loading: false, success: false, error: null },
  })

  // Email configuration
  const [emailConfig, setEmailConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: '',
    use_tls: true,
    use_ssl: false,
    reply_to: '',
    system_email_language: 'fr' as 'fr' | 'en',
  })

  const [emailTest, setEmailTest] = useState({
    isTesting: false,
    testResult: null as 'success' | 'error' | null,
    testMessage: ''
  })


  const [emailLoading, setEmailLoading] = useState(false)


  // Change password modal state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [changePasswordStatus, setChangePasswordStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [changePasswordError, setChangePasswordError] = useState<string>('')

  // Handle password change
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setChangePasswordError('')
    setChangePasswordStatus('idle')
    setIsChangingPassword(true)

    // Validate passwords match
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setChangePasswordError(t('settings.passwordsDoNotMatch'))
      setChangePasswordStatus('error')
      setIsChangingPassword(false)
      return
    }

    // Validate password length
    if (passwordForm.new_password.length < 8) {
      setChangePasswordError(t('settings.passwordMinLength'))
      setChangePasswordStatus('error')
      setIsChangingPassword(false)
      return
    }

    try {
      await authApi.changePassword({
        old_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
        new_password_confirm: passwordForm.confirm_password
      })

      setChangePasswordStatus('success')
      // Reset form
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      })

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowChangePasswordModal(false)
        setChangePasswordStatus('idle')
        setChangePasswordError('')
      }, 2000)
    } catch (error: any) {
      console.error('Error changing password:', error)
      const errorMessage = error.response?.data?.old_password?.[0] ||
        error.response?.data?.new_password_confirm?.[0] ||
        error.response?.data?.new_password?.[0] ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to change password. Please try again.'
      setChangePasswordError(errorMessage)
      setChangePasswordStatus('error')
    } finally {
      setIsChangingPassword(false)
    }
  }

  const loadUserProfile = useCallback(async () => {
    try {
      const response = await authApi.getProfile()
      const userData = response.data
      setCurrentUser(userData)
      setProfile({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        username: userData.username || '',
        email: userData.email || '',
        phone: userData.phone || '',
        company: userData.company || '',
        // calendly_link: userData.calendly_link || '' // Calendly integration disabled
      })
    } catch (error) {
      console.error('Error loading user profile:', error)
      if (user) {
        setProfile({
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          username: user.username || '',
          email: user.email || '',
          phone: user.phone || '',
          company: user.company || '',
          // calendly_link: (user as any)?.calendly_link || '' // Calendly integration disabled
        })
      }
    }
  }, [user])

  const loadDataStats = useCallback(async () => {
    try {
      const [candidatesRes, offersRes] = await Promise.all([
        candidatesApi.getAll(),
        offersApi.getAll()
      ])
      setDataStats({
        candidates: candidatesRes.data.results?.length || 0,
        offers: offersRes.data.results?.length || 0,
        applications: 0,
        lastBackup: localStorage.getItem('lastBackup')
      })
    } catch (error) {
      console.error('Error loading data stats:', error)
    }
  }, [])

  const loadEmailData = useCallback(async () => {
    setEmailLoading(true)
    try {
      const configResponse = await emailConfigApi.getConfig()
      const configs = configResponse.data?.results || (Array.isArray(configResponse.data) ? configResponse.data : [])
      const config = configs.find((c: any) => c.is_active) || configs[0]

      if (config) {
        setEmailConfig({
          smtp_host: config.smtp_host,
          smtp_port: config.smtp_port,
          smtp_username: config.smtp_username,
          smtp_password: '',
          from_email: config.from_email,
          from_name: config.from_name,
          use_tls: config.use_tls,
          use_ssl: config.use_ssl,
          reply_to: config.reply_to || '',
          system_email_language: config.system_email_language || 'fr',
        })
      }
    } catch (error) {
      console.error('Error loading email data:', error)
    } finally {
      setEmailLoading(false)
    }
  }, [])

  const loadNotificationPreferences = useCallback(async () => {
    try {
      const res = await preferencesApi.getPreferences()
      setNotifications({
        email: res.data.email,
        push: res.data.push,
        weeklyReports: res.data.weekly_reports,
        matchingUpdates: res.data.matching_updates,
        candidateUpdates: res.data.candidate_updates,
        offerUpdates: res.data.offer_updates,
      })
    } catch (error) {
      console.error('Erreur lors du chargement des préférences de notification:', error)
    }
  }, [user])

  const loadUserSettings = useCallback(async () => {
    try {
      const res = await authApi.getSessionTimeout()
      if (res?.data?.session_timeout) {
        const value = res.data.session_timeout
        setSessionTimeout(SESSION_TIMEOUT_OPTIONS.includes(value) ? value : 240)
      }
    } catch (error) {
      console.error('Error loading user settings:', error)
    }
  }, [user])

  const loadDashboardSettings = useCallback(async () => {
    if (!user?.organization && !user?.is_superuser) return
    if (dashboardSettingsLoadedRef.current) return

    const shouldShowLoading = manualMatchingTimeMinutes === null
    if (shouldShowLoading) {
      setIsLoadingDashboardSettings(true)
    }

    try {
      const res = await applicationsApi.getMatchingDashboardSettings()
      const minutes = res.data.manual_matching_time_minutes
      if (typeof minutes === 'number' && minutes > 0) {
        setManualMatchingTimeMinutes(minutes)
        updateManualMatchingTimeInDashboardCaches(minutes)
      }
      dashboardSettingsLoadedRef.current = true
    } catch (error) {
      console.error('Error loading dashboard settings:', error)
    } finally {
      if (shouldShowLoading) {
        setIsLoadingDashboardSettings(false)
      }
    }
  }, [user])

  useEffect(() => {
    // Only load data when we have a logged in user
    if (!user) return

    // Call all loaders when user changes / on mount
    loadUserProfile()
    loadDataStats()
    loadEmailData()
    loadNotificationPreferences()
    loadUserSettings()
  }, [user, loadUserProfile, loadDataStats, loadEmailData, loadNotificationPreferences, loadUserSettings])

  useEffect(() => {
    if (!user) return
    if (activeTab !== 'dashboard') return
    loadDashboardSettings()
  }, [user, activeTab, loadDashboardSettings])

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab')
    if (tabFromUrl) {
      setActiveTab(tabFromUrl)
    }
  }, [searchParams])

  // Handler to update session timeout
  const handleSessionTimeoutChange = async (minutes: number) => {
    const previous = sessionTimeout
    setSessionTimeout(minutes)
    setIsSavingSessionTimeout(true)
    try {
      await authApi.updateSessionTimeout({ session_timeout: minutes })
    } catch (error) {
      console.error('Error updating session timeout:', error)
      setSessionTimeout(previous)
    } finally {
      setIsSavingSessionTimeout(false)
    }
  }

  const handleSaveDashboardSettings = async () => {
    if (manualMatchingTimeMinutes === null) return

    const previous = manualMatchingTimeMinutes
    setIsSavingDashboardSettings(true)
    try {
      const res = await applicationsApi.updateMatchingDashboardSettings({
        manual_matching_time_minutes: manualMatchingTimeMinutes,
      })
      setManualMatchingTimeMinutes(res.data.manual_matching_time_minutes)
      updateManualMatchingTimeInDashboardCaches(res.data.manual_matching_time_minutes)
      dashboardSettingsLoadedRef.current = true
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('settings.dashboardSettings.savedSuccessfully'),
      })
    } catch (error: any) {
      console.error('Error updating dashboard settings:', error)
      setManualMatchingTimeMinutes(previous)
      const apiError = error?.response?.data?.error || error?.response?.data?.detail
      addToast({
        type: 'error',
        title: t('common.error'),
        message: apiError || t('settings.dashboardSettings.saveFailed'),
      })
    } finally {
      setIsSavingDashboardSettings(false)
    }
  }




  const handleNotificationChange = async (key: string, value: boolean) => {
    // Optimistic update
    const previous = notifications
    setNotifications((prev) => ({ ...prev, [key]: value }))

    const payload: Record<string, boolean> = {}
    // Map UI keys to API keys
    if (key === 'weeklyReports') payload['weekly_reports'] = value
    else if (key === 'matchingUpdates') payload['matching_updates'] = value
    else if (key === 'candidateUpdates') payload['candidate_updates'] = value
    else if (key === 'offerUpdates') payload['offer_updates'] = value
    else payload[key] = value

    try {
      await preferencesApi.updatePreferences(payload)
    } catch (error) {
      console.error('Erreur lors de la mise à jour des préférences:', error)
      // Revert optimistic update on error
      setNotifications(previous)
    }
  }




  const handleSaveProfile = async () => {
    setIsSaving(true)
    setSaveStatus('idle')

    try {
      // Update profile using backend API
      const response = await authApi.updateProfile(profile)
      // Backend returns { message: string, user: User }
      const responseData = response.data as any
      if (responseData.user) {
        setCurrentUser(responseData.user)
      } else {
        // Fallback if response structure is different
        setCurrentUser(response.data as User)
      }
      setSaveStatus('success')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch (error) {
      console.error('Error updating profile:', error)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } finally {
      setIsSaving(false)
    }
  }

  const handleExport = async (model: 'candidates' | 'offers' | 'users' | 'matchings' | 'hiring_process', format: 'json' | 'csv' = 'json') => {
    setExportStates(prev => ({ ...prev, [model]: { loading: true, success: false, error: null } }))
    
    try {
      let response
      switch (model) {
        case 'candidates':
          response = await dataExportApi.exportCandidates(format, false)
          break
        case 'offers':
          response = await dataExportApi.exportOffers(format, false)
          break
        case 'users':
          response = await dataExportApi.exportUsers(format, false)
          break
        case 'matchings':
          response = await dataExportApi.exportMatchings(format, false)
          break
        case 'hiring_process':
          response = await dataExportApi.exportHiringProcess(format, false)
          break
      }

      // Handle blob response
      const blob = new Blob([response.data], { 
        type: format === 'json' ? 'application/json' : 'text/csv' 
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const extension = format === 'json' ? 'json' : 'csv'
      const modelName = model === 'hiring_process' ? 'hiring_process' : model
      a.download = `${modelName}_export_${new Date().toISOString().split('T')[0]}.${extension}`
      a.click()
      URL.revokeObjectURL(url)

      setExportStates(prev => ({ ...prev, [model]: { loading: false, success: true, error: null } }))
      setTimeout(() => {
        setExportStates(prev => ({ ...prev, [model]: { loading: false, success: false, error: null } }))
      }, 3000)

      addToast({
        type: 'success',
        title: t('settings.exportSuccess', { defaultValue: 'Export Successful' }),
        message: t('settings.exportSuccessMessage', { defaultValue: `Successfully exported ${model}` }),
        duration: 3000
      })
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || t('settings.exportError', { defaultValue: 'Failed to export data' })
      setExportStates(prev => ({ ...prev, [model]: { loading: false, success: false, error: errorMessage } }))
      
      addToast({
        type: 'error',
        title: t('settings.exportError', { defaultValue: 'Export Failed' }),
        message: errorMessage,
        duration: 5000
      })
    }
  }

  const handleBackupData = async () => {
    setExportStates(prev => ({ ...prev, backup: { loading: true, success: false, error: null } }))
    
    try {
      const response = await dataExportApi.backupAllData()

      // Handle blob response
      const blob = new Blob([response.data], { type: 'application/zip' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `backup_all_data_${new Date().toISOString().split('T')[0]}.zip`
      a.click()
      URL.revokeObjectURL(url)

      localStorage.setItem('lastBackup', new Date().toISOString())
      setDataStats(prev => ({ ...prev, lastBackup: new Date().toISOString() }))
      
      setExportStates(prev => ({ ...prev, backup: { loading: false, success: true, error: null } }))
      setTimeout(() => {
        setExportStates(prev => ({ ...prev, backup: { loading: false, success: false, error: null } }))
      }, 3000)

      addToast({
        type: 'success',
        title: t('settings.backupSuccess', { defaultValue: 'Backup Successful' }),
        message: t('settings.backupSuccessMessage', { defaultValue: 'Successfully created backup of all data' }),
        duration: 3000
      })
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || t('settings.backupError', { defaultValue: 'Failed to create backup' })
      setExportStates(prev => ({ ...prev, backup: { loading: false, success: false, error: errorMessage } }))
      
      addToast({
        type: 'error',
        title: t('settings.backupError', { defaultValue: 'Backup Failed' }),
        message: errorMessage,
        duration: 5000
      })
    }
  }

  const handleSaveEmailConfig = async () => {
    setIsSaving(true)
    setSaveStatus('idle')

    try {
      const configResponse = await emailConfigApi.getConfig()

      // Get active configuration or first one (global config)
      const configs = configResponse.data?.results || (Array.isArray(configResponse.data) ? configResponse.data : [])
      const existingConfig = configs.find((c: any) => c.is_active) || configs[0]

      if (existingConfig) {
        // Update existing configuration (global)
        const updateData = { ...emailConfig, is_active: true }

        // If password is empty, don't include it in the update
        if (!updateData.smtp_password) {
          const { smtp_password, ...updateDataWithoutPassword } = updateData
          await emailConfigApi.updateConfig(existingConfig.id!, updateDataWithoutPassword)
        } else {
          await emailConfigApi.updateConfig(existingConfig.id!, updateData)
        }
      } else {
        // Create new configuration (global, automatically set as active)
        await emailConfigApi.createConfig({ ...emailConfig, is_active: true })
      }

      setSaveStatus('success')
      setTimeout(() => setSaveStatus('idle'), 3000)

      // Reload the configuration to get the updated data
      loadEmailData()
    } catch (error) {
      console.error('Error saving email config:', error)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } finally {
      setIsSaving(false)
    }
  }

  const handleTestEmail = async () => {
    setEmailTest(prev => ({ ...prev, isTesting: true, testResult: null, testMessage: '' }))

    try {
      // Include current form config in test request (even if not saved)
      const testPayload: any = {
        to_email: user?.email || emailConfig.from_email,
        subject: 'Test Email from ATS',
        message: 'This is a test email to verify your SMTP configuration.',
        // Include current config from form
        smtp_host: emailConfig.smtp_host,
        smtp_port: emailConfig.smtp_port,
        smtp_username: emailConfig.smtp_username,
        smtp_password: emailConfig.smtp_password,
        from_email: emailConfig.from_email,
        from_name: emailConfig.from_name,
        use_tls: emailConfig.use_tls,
        use_ssl: emailConfig.use_ssl
      }

      const response = await emailConfigApi.testConfig(testPayload)

      setEmailTest({
        isTesting: false,
        testResult: response.data.status,
        testMessage: response.data.message || response.data.error_details || response.data.error || 'Unknown error'
      })

      setTimeout(() => {
        setEmailTest(prev => ({ ...prev, testResult: null, testMessage: '' }))
      }, 8000) // Show error messages longer
    } catch (error: any) {
      console.error('Error testing email:', error)
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error_details ||
        error.response?.data?.error ||
        error.message ||
        'Failed to send test email. Please check your configuration.'

      setEmailTest({
        isTesting: false,
        testResult: 'error',
        testMessage: errorMessage
      })

      setTimeout(() => {
        setEmailTest(prev => ({ ...prev, testResult: null, testMessage: '' }))
      }, 8000) // Show error messages longer
    }
  }


  // Two-Factor Authentication (2FA) state and handler
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)
  const [email2FAEnabled, setEmail2FAEnabled] = useState(false)
  const [showQRModal, setShowQRModal] = useState(false)
  const [qrCodeData, setQrCodeData] = useState('')
  const [isResetting2FA, setIsResetting2FA] = useState(false)
  const [isQRCodeReset, setIsQRCodeReset] = useState(false)
  const [showReset2FAModal, setShowReset2FAModal] = useState(false)
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [showBackupCodesModal, setShowBackupCodesModal] = useState(false)
  const [unusedBackupCodesCount, setUnusedBackupCodesCount] = useState(0)

  const handleToggle2FA = async (checked: boolean) => {
    try {
      // When enabling, default email to true (both enabled by default)
      const res = await authApi.toggle2FA(checked, checked ? true : email2FAEnabled);

      if (checked) {
        setTwoFactorEnabled(true);
        // Email is enabled by default when 2FA is enabled
        setEmail2FAEnabled(res.data.email_enabled !== false);

        // Always show QR code for authenticator app
        if (res.data.qr) {
          setQrCodeData(res.data.qr);
          setShowQRModal(true);
          setIsQRCodeReset(false);
        }

        // Store backup codes if provided
        if (res.data.backup_codes && res.data.backup_codes.length > 0) {
          setBackupCodes(res.data.backup_codes);
          setShowBackupCodesModal(true);
        }
      } else {
        setTwoFactorEnabled(false);
        setEmail2FAEnabled(false);
        setShowQRModal(false);
        setIsQRCodeReset(false);
        setBackupCodes([]);
        setUnusedBackupCodesCount(0);
      }
    } catch (error: any) {
      console.error('2FA toggle error:', error);
      setTwoFactorEnabled(!checked);
      addToast({
        type: 'error',
        title: t('common.error'),
        message: error.response?.data?.error || t('settings.errorUpdating2FA'),
        duration: 5000
      })
    }
  };

  const handleToggleEmail2FA = async (checked: boolean) => {
    try {
      // Toggle email 2FA while keeping authenticator enabled
      const res = await authApi.toggle2FA(true, checked);
      setEmail2FAEnabled(checked);
    } catch (error: any) {
      console.error('Email 2FA toggle error:', error);
      setEmail2FAEnabled(!checked);
      addToast({
        type: 'error',
        title: t('common.error'),
        message: error.response?.data?.error || t('settings.errorUpdatingEmail2FA'),
        duration: 5000
      })
    }
  };

  const handleReset2FA = async () => {
    setIsResetting2FA(true);
    try {
      const res = await authApi.reset2FA();
      if (res.data.qr) {
        setQrCodeData(res.data.qr);
        setIsQRCodeReset(true); // This is a reset, show warning
        setShowQRModal(true);
        setShowReset2FAModal(false);
        // Store new backup codes
        if (res.data.backup_codes && res.data.backup_codes.length > 0) {
          setBackupCodes(res.data.backup_codes);
          setShowBackupCodesModal(true);
        }
      }
    } catch (error: any) {
      console.error('2FA reset error:', error);
      addToast({
        type: 'error',
        title: t('common.error'),
        message: error.response?.data?.error || t('settings.errorResetting2FA'),
        duration: 5000
      })
    } finally {
      setIsResetting2FA(false);
    }
  };

  // Load 2FA status on mount
  useEffect(() => {
    const load2FAStatus = async () => {
      try {
        const response = await authApi.get2FAStatus();
        setTwoFactorEnabled(response.data.two_factor_enabled);
        // If 2FA is enabled but email status not set, default to true (both enabled by default)
        setEmail2FAEnabled(response.data.two_factor_enabled ? (response.data.email_enabled !== false) : false);

        // Load backup codes count if 2FA is enabled
        if (response.data.two_factor_enabled) {
          try {
            const backupCodesResponse = await authApi.getBackupCodes();
            setUnusedBackupCodesCount(backupCodesResponse.data.unused_codes_count);
          } catch (error) {
            console.error('Error loading backup codes count:', error);
          }
        }
      } catch (error) {
        console.error('Error loading 2FA status:', error);
      }
    };
    if (user) {
      load2FAStatus();
    }
  }, [user]);

  // Check if user is in a regular organization (system organization users should see all tabs)
  // Users in Selektia system organization (domain: 'selektia-system') or superusers should see all tabs
  const userOrg = user?.organization
  const isSelektiaSystemOrg =
    user?.is_superuser || // Superusers are in Selektia system org
    (typeof userOrg === 'object' && userOrg !== null && userOrg.domain === 'selektia-system')
  // Regular organization users (not Selektia system org) should not see certain tabs
  const isOrganizationUser = Boolean(user?.organization) && !isSelektiaSystemOrg

  // Fetch user roles to check if they have Admin role
  // Admin role should see all tabs, not be restricted
  useEffect(() => {
    const checkAdminRole = async () => {
      if (user?.id && isOrganizationUser) {
        try {
          setIsLoadingRoles(true)
          const response = await rolesApi.getUserRoles(user.id)
          const userRoles: UserRole[] = Array.isArray(response.data) ? response.data : []
          
          console.log('Checking Admin role - User roles:', userRoles)
          
          // Check if user has "Admin" role (case-insensitive, organization type)
          // Also check is_staff as a fallback for organization admins
          const hasAdminRole = userRoles.some((role: UserRole) => {
            const roleNameLower = role.role_name?.toLowerCase() || ''
            const isAdminRole = roleNameLower === 'admin' && role.role_type === 'organization'
            console.log(`Role: ${role.role_name}, type: ${role.role_type}, isAdmin: ${isAdminRole}`)
            return isAdminRole
          })
          
          // Also check is_staff as organization admins typically have is_staff = true
          const isStaffAdmin = Boolean(user?.is_staff && !user?.is_superuser && user?.organization)
          
          const isAdmin = hasAdminRole || isStaffAdmin
          console.log('Final Admin check - hasAdminRole:', hasAdminRole, 'isStaffAdmin:', isStaffAdmin, 'isAdmin:', isAdmin)
          setIsOrgAdmin(isAdmin)
        } catch (error) {
          console.error('Error fetching user roles:', error)
          // Fallback: check is_staff if role fetch fails
          const isStaffAdmin = Boolean(user?.is_staff && !user?.is_superuser && user?.organization)
          setIsOrgAdmin(isStaffAdmin)
        } finally {
          setIsLoadingRoles(false)
        }
      } else {
        // For system org users or superusers, they are considered admin
        setIsOrgAdmin(isSelektiaSystemOrg || user?.is_superuser || false)
        setIsLoadingRoles(false)
      }
    }

    checkAdminRole()
  }, [user, isOrganizationUser, isSelektiaSystemOrg])

  const allTabs = [
    { id: 'profile', label: t('settings.tabs.profile'), icon: UserIcon },
    { id: 'notifications', label: t('settings.tabs.notifications'), icon: Bell },
    { id: 'email', label: t('settings.tabs.emailOutgoing'), icon: Mail },
    { id: 'dashboard', label: t('settings.tabs.dashboard'), icon: BarChart3 },
    { id: 'ai', label: t('settings.aiSettings.title'), icon: Brain },
    { id: 'data', label: t('settings.tabs.dataManagement'), icon: Database },
    { id: 'security', label: t('settings.tabs.security'), icon: Shield },
    { id: 'system', label: t('settings.tabs.system'), icon: SettingsIcon }
  ]

  // Filter tabs based on user role and permissions:
  // - System org users/superusers: see all tabs
  // - Organization Admin role OR users with admin permissions: see Profile, Email, Data Management, Security
  // - Other organization roles (non-Admin): only Profile and Security
  // Check permissions as fallback for admins who might not have role properly set
  // If user has export_data or backup_restore permissions, they're likely an admin
  const hasAdminPermissions = hasPermission('export_data') || hasPermission('backup_restore')
  const shouldShowAdminTabs = isOrgAdmin || hasAdminPermissions
  
  const tabs = isSelektiaSystemOrg
    ? allTabs  // System users see all tabs
    : shouldShowAdminTabs && isOrganizationUser
    ? allTabs.filter(tab => ['profile', 'email', 'dashboard', 'data', 'security'].includes(tab.id))  // Org Admin sees Profile, Email, Dashboard, Data, Security
    : isOrganizationUser
    ? allTabs.filter(tab => ['profile', 'security'].includes(tab.id))  // Non-admin org users only see Profile and Security
    : allTabs

  // Redirect to profile if current tab is restricted
  useEffect(() => {
    if (isOrganizationUser && !isLoadingRoles) {
      // Check permissions as fallback for admins who might not have role properly set
      // If user has export_data or backup_restore permissions, they're likely an admin
      const hasAdminPermissions = hasPermission('export_data') || hasPermission('backup_restore')
      const shouldShowAdminTabs = isOrgAdmin || hasAdminPermissions
      
      if (shouldShowAdminTabs) {
        // Org Admin can only access: profile, email, data, security
        if (!['profile', 'email', 'dashboard', 'data', 'security'].includes(activeTab)) {
          setActiveTab('profile')
        }
      } else {
        // Non-admin org users can only access: profile, security
        if (!['profile', 'security'].includes(activeTab)) {
          setActiveTab('profile')
        }
      }
    }
  }, [isOrganizationUser, isOrgAdmin, isLoadingRoles, activeTab, hasPermission])

  const renderTabContent = () => {
    // Prevent organization users from accessing restricted tabs
    if (isOrganizationUser && !isLoadingRoles) {
      // Check permissions as fallback for admins who might not have role properly set
      // If user has export_data or backup_restore permissions, they're likely an admin
      const hasAdminPermissions = hasPermission('export_data') || hasPermission('backup_restore')
      const shouldShowAdminTabs = isOrgAdmin || hasAdminPermissions
      
      if (shouldShowAdminTabs) {
        // Org Admin can only access: profile, email, data, security
        if (!['profile', 'email', 'dashboard', 'data', 'security'].includes(activeTab)) {
          return (
            <div className="space-y-6 max-[1149px]:space-y-4">
              <Card className="max-[1149px]:border">
                <CardContent className="p-6 text-center max-[1149px]:p-4">
                  <p className="text-muted-foreground">{t('settings.accessRestricted', 'This section is not available for your role.')}</p>
                </CardContent>
              </Card>
            </div>
          )
        }
      } else {
        // Non-admin org users can only access: profile, security
        if (!['profile', 'security'].includes(activeTab)) {
          return (
            <div className="space-y-6 max-[1149px]:space-y-4">
              <Card className="max-[1149px]:border">
                <CardContent className="p-6 text-center max-[1149px]:p-4">
                  <p className="text-muted-foreground">{t('settings.accessRestricted', 'This section is only available for administrators.')}</p>
                </CardContent>
              </Card>
            </div>
          )
        }
      }
    }

    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6 max-[1149px]:space-y-4">
            <Card className="max-[1149px]:border">
              <CardHeader className="max-[1149px]:p-4">
                <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                  <UserIcon className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                  <span>{t('settings.profile.title')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 max-[1149px]:p-4 max-[1149px]:space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-[1149px]:gap-3">
                  <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.profile.firstName')}</label>
                    <Input
                      value={profile.first_name}
                      onChange={(e) => setProfile(prev => ({ ...prev, first_name: e.target.value }))}
                      placeholder={t('settings.profile.firstNamePlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                  </div>
                  <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.profile.lastName')}</label>
                    <Input
                      value={profile.last_name}
                      onChange={(e) => setProfile(prev => ({ ...prev, last_name: e.target.value }))}
                      placeholder={t('settings.profile.lastNamePlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                  </div>
                  <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.profile.username')}</label>
                    <Input
                      value={profile.username}
                      onChange={(e) => setProfile(prev => ({ ...prev, username: e.target.value }))}
                      placeholder={t('settings.profile.usernamePlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                  </div>
                  <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.profile.email')}</label>
                    <Input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                      placeholder={t('settings.profile.emailPlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                  </div>
                  <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.profile.phone')}</label>
                    <Input
                      value={profile.phone}
                      onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder={t('settings.profile.phonePlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                  </div>
                  {/* Company field hidden */}
                  {/* <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.profile.company')}</label>
                    <Input
                      value={profile.company}
                      onChange={(e) => setProfile(prev => ({ ...prev, company: e.target.value }))}
                      placeholder={t('settings.profile.companyPlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                  </div> */}
                  {/* Calendly profile field temporarily disabled */}
                  {/* <div className="space-y-2 max-[1149px]:space-y-1.5 md:col-span-2">
                    <label className="text-sm font-medium max-[1149px]:text-xs">
                      {t('settings.profile.calendlyLink', 'Calendly Link')}
                    </label>
                    <Input
                      type="url"
                      value={profile.calendly_link}
                      onChange={(e) => setProfile(prev => ({ ...prev, calendly_link: e.target.value }))}
                      placeholder="https://calendly.com/your-name"
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                    <p className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                      {t('settings.profile.calendlyLinkDescription', 'Your Calendly link will be included in interview scheduling emails')}
                    </p>
                  </div> */}
                </div>
                <div className="flex items-center justify-between max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-3">
                  <div className="flex items-center space-x-2 max-[1149px]:justify-center">
                    {saveStatus === 'success' && (
                      <div className="flex items-center text-green-600 dark:text-green-400 max-[1149px]:text-xs">
                        <CheckCircle className="h-4 w-4 mr-1 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                        <span className="text-sm max-[1149px]:text-xs">Profile saved successfully</span>
                      </div>
                    )}
                    {saveStatus === 'error' && (
                      <div className="flex items-center text-red-600 dark:text-red-400 max-[1149px]:text-xs">
                        <AlertTriangle className="h-4 w-4 mr-1 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                        <span className="text-sm max-[1149px]:text-xs">Failed to save profile</span>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={handleSaveProfile}
                    disabled={isSaving}
                    className="flex items-center space-x-2 max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm max-[1149px]:justify-center"
                  >
                    {isSaving ? (
                      <RefreshCw className="h-4 w-4 animate-spin max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    ) : (
                      <Save className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    )}
                    <span>{isSaving ? t('settings.saving') : t('settings.saveProfile')}</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="max-[1149px]:border">
              <CardHeader className="max-[1149px]:p-4">
                <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                  <Palette className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                  <span>{t('settings.appearance')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 max-[1149px]:p-4 max-[1149px]:space-y-3">
                <div className="flex items-center justify-between max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-2">
                  <div className="max-[1149px]:flex-1">
                    <p className="font-medium max-[1149px]:text-sm">{t('settings.theme')}</p>
                    <p className="text-sm text-muted-foreground max-[1149px]:text-xs">
                      {t('settings.themeDescription')}
                    </p>
                  </div>
                  <Button variant="outline" onClick={toggleTheme} className="max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm">
                    {theme === 'light' ? (
                      <>
                        <Sun className="h-4 w-4 mr-2 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                        {t('settings.light')}
                      </>
                    ) : (
                      <>
                        <Moon className="h-4 w-4 mr-2 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                        {t('settings.dark')}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )

      case 'notifications':
        return <NotificationPreferences />

      case 'email':
        return (
          <div className="space-y-6 max-[1149px]:space-y-4">
            <Card className="max-[1149px]:border">
              <CardHeader className="max-[1149px]:p-4">
                <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                  <Server className="h-5 w-5" />
                  <span>{t('settings.emailConfig.title')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 max-[1149px]:p-4 max-[1149px]:space-y-3">
                <div className="pb-4 max-[1149px]:pb-3 space-y-3 border-b">
                  <div>
                    <h3 className="text-sm font-semibold max-[1149px]:text-xs">
                      {t('settings.emailConfig.systemEmailLanguageTitle')}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 max-[1149px]:text-xs">
                      {t('settings.emailConfig.systemEmailLanguageDescription')}
                    </p>
                  </div>
                  <div className="space-y-2 max-[1149px]:space-y-1.5 max-w-md">
                    <label className="text-sm font-medium max-[1149px]:text-xs">
                      {t('settings.emailConfig.systemEmailLanguageLabel')}
                    </label>
                    <select
                      value={emailConfig.system_email_language}
                      onChange={(e) =>
                        setEmailConfig(prev => ({
                          ...prev,
                          system_email_language: e.target.value as 'fr' | 'en',
                        }))
                      }
                      className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm max-[1149px]:h-9"
                    >
                      <option value="fr">{t('settings.emailConfig.systemEmailLanguageFrench')}</option>
                      <option value="en">{t('settings.emailConfig.systemEmailLanguageEnglish')}</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-[1149px]:gap-3">
                  <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.emailConfig.smtpServer')}</label>
                    <Input
                      value={emailConfig.smtp_host}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, smtp_host: e.target.value }))}
                      placeholder={t('settings.email.smtpHostPlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                  </div>
                  <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.emailConfig.port')}</label>
                    <Input
                      type="number"
                      value={emailConfig.smtp_port}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                      placeholder={t('settings.email.smtpPortPlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                  </div>
                  <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.emailConfig.username')}</label>
                    <Input
                      value={emailConfig.smtp_username}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, smtp_username: e.target.value }))}
                      placeholder={t('settings.email.smtpUsernamePlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                  </div>
                  <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.emailConfig.password')}</label>
                    <Input
                      type="password"
                      value={emailConfig.smtp_password}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, smtp_password: e.target.value }))}
                      placeholder={t('settings.email.smtpPasswordPlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                    {emailConfig.smtp_password === '' && (
                      <p className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                        {t('settings.emailConfig.passwordNotShown')}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.emailConfig.fromEmail')}</label>
                    <Input
                      type="email"
                      value={emailConfig.from_email}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, from_email: e.target.value }))}
                      placeholder={t('settings.email.fromEmailPlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                  </div>
                  <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.emailConfig.fromName')}</label>
                    <Input
                      value={emailConfig.from_name}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, from_name: e.target.value }))}
                      placeholder={t('settings.email.fromNamePlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                  </div>
                  <div className="space-y-2 max-[1149px]:space-y-1.5">
                    <label className="text-sm font-medium max-[1149px]:text-xs">{t('settings.emailConfig.replyTo')}</label>
                    <Input
                      type="email"
                      value={emailConfig.reply_to}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, reply_to: e.target.value }))}
                      placeholder={t('settings.email.replyToPlaceholder')}
                      className="max-[1149px]:h-9 max-[1149px]:text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={emailConfig.use_tls}
                        onChange={(e) => setEmailConfig(prev => ({
                          ...prev,
                          use_tls: e.target.checked,
                          use_ssl: e.target.checked ? false : prev.use_ssl
                        }))}
                        className="rounded"
                      />
                      <label className="text-sm font-medium max-[1149px]:text-xs">Utiliser TLS</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={emailConfig.use_ssl}
                        onChange={(e) => setEmailConfig(prev => ({
                          ...prev,
                          use_ssl: e.target.checked,
                          use_tls: e.target.checked ? false : prev.use_tls
                        }))}
                        className="rounded"
                      />
                      <label className="text-sm font-medium max-[1149px]:text-xs">Utiliser SSL</label>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t max-[1149px]:pt-3 max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-3">
                  <div className="flex items-center space-x-2 max-[1149px]:justify-center">
                    {saveStatus === 'success' && (
                      <div className="flex items-center text-green-600 dark:text-green-400 max-[1149px]:text-xs">
                        <CheckCircle className="h-4 w-4 mr-1 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                        <span className="text-sm max-[1149px]:text-xs">Configuration sauvegardée</span>
                      </div>
                    )}
                    {saveStatus === 'error' && (
                      <div className="flex items-center text-red-600 dark:text-red-400 max-[1149px]:text-xs">
                        <AlertTriangle className="h-4 w-4 mr-1 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                        <span className="text-sm max-[1149px]:text-xs">Erreur de sauvegarde</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 max-[1149px]:w-full max-[1149px]:flex-col max-[1149px]:space-y-2 max-[1149px]:space-x-0">
                    <Button
                      onClick={handleTestEmail}
                      disabled={emailTest.isTesting}
                      variant="outline"
                      className="flex items-center space-x-2 max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm max-[1149px]:justify-center"
                    >
                      {emailTest.isTesting ? (
                        <RefreshCw className="h-4 w-4 animate-spin max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                      ) : (
                        <TestTube className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                      )}
                      <span>{emailTest.isTesting ? t('settings.emailConfig.testing') : t('settings.emailConfig.test')}</span>
                    </Button>
                    <Button
                      onClick={handleSaveEmailConfig}
                      disabled={isSaving}
                      className="flex items-center space-x-2 max-[1149px]:w-full max-[1149px]:h-9 max-[1149px]:text-sm max-[1149px]:justify-center"
                    >
                      {isSaving ? (
                        <RefreshCw className="h-4 w-4 animate-spin max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                      ) : (
                        <Save className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                      )}
                      <span>{isSaving ? t('settings.emailConfig.saving') : t('settings.emailConfig.save')}</span>
                    </Button>
                  </div>
                </div>

                {emailTest.testResult && (
                  <div className={`p-3 rounded-lg ${emailTest.testResult === 'success'
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                    }`}>
                    <div className="flex items-center">
                      {emailTest.testResult === 'success' ? (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 mr-2" />
                      )}
                      <span className="text-sm">{emailTest.testMessage}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <OutgoingEmailLogTable />
          </div>
        )

      case 'ai':
        return (
          <Card className="max-[1149px]:border">
            <CardHeader className="max-[1149px]:p-4">
              <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                <Brain className="h-5 w-5" />
                <span>{t('settings.aiSettings.title')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <AIConfigSection />
            </CardContent>
          </Card>
        )



      case 'dashboard': {
        const dashboardHasAdminPermissions = hasPermission('export_data') || hasPermission('backup_restore')
        const canManageDashboardSettings = isSelektiaSystemOrg || isOrgAdmin || dashboardHasAdminPermissions

        if (isOrganizationUser && !canManageDashboardSettings) {
          return (
            <div className="space-y-6 max-[1149px]:space-y-4">
              <Card className="max-[1149px]:border">
                <CardContent className="p-6 text-center max-[1149px]:p-4">
                  <p className="text-muted-foreground">{t('settings.accessRestricted', 'This section is only available for organization administrators.')}</p>
                </CardContent>
              </Card>
            </div>
          )
        }

        return (
          <div className="space-y-6 max-[1149px]:space-y-4">
            <Card className="max-[1149px]:border">
              <CardHeader className="max-[1149px]:p-4">
                <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                  <BarChart3 className="h-5 w-5" />
                  <span>{t('settings.dashboardSettings.title')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 max-[1149px]:p-4">
                <div className="space-y-2">
                  <label htmlFor="manual-matching-time" className="text-sm font-medium">
                    {t('settings.dashboardSettings.manualMatchingTimeLabel')}
                  </label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.dashboardSettings.manualMatchingTimeDescription')}
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Input
                      id="manual-matching-time"
                      type="number"
                      min={1}
                      max={999}
                      value={manualMatchingTimeMinutes ?? ''}
                      placeholder={isLoadingDashboardSettings ? t('settings.dashboardSettings.loading') : undefined}
                      onChange={(e) => {
                        const next = Number(e.target.value)
                        if (Number.isFinite(next) && next > 0) {
                          setManualMatchingTimeMinutes(next)
                        }
                      }}
                      className="w-full sm:max-w-[160px]"
                      disabled={isSavingDashboardSettings || (isLoadingDashboardSettings && manualMatchingTimeMinutes === null)}
                    />
                    <Button
                      onClick={handleSaveDashboardSettings}
                      disabled={isSavingDashboardSettings || manualMatchingTimeMinutes === null || isLoadingDashboardSettings}
                    >
                      {isSavingDashboardSettings ? t('settings.saving') : t('settings.dashboardSettings.save')}
                    </Button>
                  </div>
                  {isLoadingDashboardSettings && manualMatchingTimeMinutes === null && (
                    <p className="text-xs text-muted-foreground">{t('settings.dashboardSettings.loading')}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )
      }

      case 'data':
        // Only organization admins can access this section
        // Check permissions as fallback for admins who might not have role properly set
        // If user has export_data or backup_restore permissions, they're likely an admin
        const hasAdminPermissions = hasPermission('export_data') || hasPermission('backup_restore')
        const shouldShowAdminTabs = isOrgAdmin || hasAdminPermissions
        
        if (isOrganizationUser && !shouldShowAdminTabs) {
        return (
          <div className="space-y-6 max-[1149px]:space-y-4">
            <Card className="max-[1149px]:border">
                <CardContent className="p-6 text-center max-[1149px]:p-4">
                  <p className="text-muted-foreground">{t('settings.accessRestricted', 'This section is only available for organization administrators.')}</p>
              </CardContent>
            </Card>
            </div>
          )
        }

        return (
          <div className="space-y-6 max-[1149px]:space-y-4">
            {(!hasPermission('export_data') || !hasPermission('backup_restore')) && (
              <Card className="max-[1149px]:border">
                <CardContent className="p-4">
                  <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 text-sm space-y-1">
                    {!hasPermission('export_data') && (
                      <div>{t('settings.exportPermissionRequired', { defaultValue: 'You do not have permission to export data.' })}</div>
                    )}
                    {!hasPermission('backup_restore') && (
                      <div>{t('settings.backupPermissionRequired', { defaultValue: 'You do not have permission to backup/restore data.' })}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Grid Layout for Export Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-[1149px]:gap-3">
              {/* Export Candidates Card */}
              <Card className="max-[1149px]:border">
                <CardHeader className="p-4 pb-3 max-[1149px]:p-3 max-[1149px]:pb-2">
                  <CardTitle className="flex items-center space-x-2 text-base max-[1149px]:text-sm">
                    <Download className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    <span>{t('settings.exportCandidates', { defaultValue: 'Export Candidates' })}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-3 max-[1149px]:p-3 max-[1149px]:pt-0 max-[1149px]:space-y-2">
                  <p className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                    {t('settings.exportCandidatesDesc', { defaultValue: 'Export all candidate data in JSON format. CSV format is also available for human-readable use.' })}
                  </p>
                  <div className="flex justify-start">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleExport('candidates', 'json')}
                    disabled={!hasPermission('export_data') || exportStates.candidates.loading}
                    className="w-auto"
                  >
                    {exportStates.candidates.loading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {t('settings.exporting', { defaultValue: 'Exporting...' })}
                      </>
                    ) : exportStates.candidates.success ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {t('settings.exported', { defaultValue: 'Exported' })}
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        {t('settings.export', { defaultValue: 'Export' })}
                      </>
                    )}
                      </Button>
                  </div>
                  {exportStates.candidates.error && (
                    <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
                      {exportStates.candidates.error}
                        </div>
                      )}
                </CardContent>
              </Card>

              {/* Export Offers Card */}
              <Card className="max-[1149px]:border">
                <CardHeader className="p-4 pb-3 max-[1149px]:p-3 max-[1149px]:pb-2">
                  <CardTitle className="flex items-center space-x-2 text-base max-[1149px]:text-sm">
                    <Download className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    <span>{t('settings.exportOffers', { defaultValue: 'Export Offers' })}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-3 max-[1149px]:p-3 max-[1149px]:pt-0 max-[1149px]:space-y-2">
                  <p className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                    {t('settings.exportOffersDesc', { defaultValue: 'Export all job offers data in JSON format. CSV format is also available for human-readable use.' })}
                  </p>
                  <div className="flex justify-start">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleExport('offers', 'json')}
                    disabled={!hasPermission('export_data') || exportStates.offers.loading}
                    className="w-auto"
                  >
                    {exportStates.offers.loading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {t('settings.exporting', { defaultValue: 'Exporting...' })}
                      </>
                    ) : exportStates.offers.success ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {t('settings.exported', { defaultValue: 'Exported' })}
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        {t('settings.export', { defaultValue: 'Export' })}
                      </>
                    )}
                  </Button>
                    </div>
                  {exportStates.offers.error && (
                    <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
                      {exportStates.offers.error}
                  </div>
                  )}
                </CardContent>
              </Card>

              {/* Export Users Card */}
              <Card className="max-[1149px]:border">
                <CardHeader className="p-4 pb-3 max-[1149px]:p-3 max-[1149px]:pb-2">
                  <CardTitle className="flex items-center space-x-2 text-base max-[1149px]:text-sm">
                    <Download className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    <span>{t('settings.exportUsers', { defaultValue: 'Export Users' })}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-3 max-[1149px]:p-3 max-[1149px]:pt-0 max-[1149px]:space-y-2">
                  <p className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                    {t('settings.exportUsersDesc', { defaultValue: 'Export all users data in JSON format. CSV format is also available for human-readable use.' })}
                  </p>
                  <div className="flex justify-start">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleExport('users', 'json')}
                    disabled={!hasPermission('export_data') || exportStates.users.loading}
                    className="w-auto"
                  >
                    {exportStates.users.loading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {t('settings.exporting', { defaultValue: 'Exporting...' })}
                      </>
                    ) : exportStates.users.success ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {t('settings.exported', { defaultValue: 'Exported' })}
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        {t('settings.export', { defaultValue: 'Export' })}
                      </>
                    )}
                      </Button>
                  </div>
                  {exportStates.users.error && (
                    <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
                      {exportStates.users.error}
                        </div>
                      )}
                </CardContent>
              </Card>

              {/* Export Matchings Card */}
              <Card className="max-[1149px]:border">
                <CardHeader className="p-4 pb-3 max-[1149px]:p-3 max-[1149px]:pb-2">
                  <CardTitle className="flex items-center space-x-2 text-base max-[1149px]:text-sm">
                    <Download className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    <span>{t('settings.exportMatchings', { defaultValue: 'Export Matchings' })}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-3 max-[1149px]:p-3 max-[1149px]:pt-0 max-[1149px]:space-y-2">
                  <p className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                    {t('settings.exportMatchingsDesc', { defaultValue: 'Export all matching jobs data in JSON format. CSV format is also available for human-readable use.' })}
                  </p>
                  <div className="flex justify-start">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleExport('matchings', 'json')}
                    disabled={!hasPermission('export_data') || exportStates.matchings.loading}
                    className="w-auto"
                  >
                    {exportStates.matchings.loading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {t('settings.exporting', { defaultValue: 'Exporting...' })}
                      </>
                    ) : exportStates.matchings.success ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {t('settings.exported', { defaultValue: 'Exported' })}
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        {t('settings.export', { defaultValue: 'Export' })}
                      </>
                    )}
                  </Button>
                    </div>
                  {exportStates.matchings.error && (
                    <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
                      {exportStates.matchings.error}
                  </div>
                  )}
                </CardContent>
              </Card>

              {/* Export Hiring Process Card */}
              <Card className="max-[1149px]:border">
                <CardHeader className="p-4 pb-3 max-[1149px]:p-3 max-[1149px]:pb-2">
                  <CardTitle className="flex items-center space-x-2 text-base max-[1149px]:text-sm">
                    <Download className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    <span>{t('settings.exportHiringProcess', { defaultValue: 'Export Hiring Process' })}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-3 max-[1149px]:p-3 max-[1149px]:pt-0 max-[1149px]:space-y-2">
                  <p className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                    {t('settings.exportHiringProcessDesc', { defaultValue: 'Export all hiring process data (templates, steps, candidate processes, step progress, actions) in JSON format. CSV format is also available for human-readable use.' })}
                  </p>
                  <div className="flex justify-start">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleExport('hiring_process', 'json')}
                    disabled={!hasPermission('export_data') || exportStates.hiring_process.loading}
                    className="w-auto"
                  >
                    {exportStates.hiring_process.loading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {t('settings.exporting', { defaultValue: 'Exporting...' })}
                      </>
                    ) : exportStates.hiring_process.success ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {t('settings.exported', { defaultValue: 'Exported' })}
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        {t('settings.export', { defaultValue: 'Export' })}
                      </>
                    )}
                  </Button>
                    </div>
                  {exportStates.hiring_process.error && (
                    <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
                      {exportStates.hiring_process.error}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Backup All Data Card */}
              <Card className="max-[1149px]:border md:col-span-2 lg:col-span-1">
                <CardHeader className="p-4 pb-3 max-[1149px]:p-3 max-[1149px]:pb-2">
                  <CardTitle className="flex items-center space-x-2 text-base max-[1149px]:text-sm">
                    <FileText className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                    <span>{t('settings.backupAllData', { defaultValue: 'Backup All Data' })}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-3 max-[1149px]:p-3 max-[1149px]:pt-0 max-[1149px]:space-y-2">
                  <p className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                    {t('settings.backupAllDataDesc', { defaultValue: 'Export all data models (candidates, offers, users, matchings, hiring process) as a ZIP archive with separate folders per model and metadata.json file.' })}
                  </p>
                  <div className="flex justify-start">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleBackupData}
                    disabled={!hasPermission('backup_restore') || exportStates.backup.loading}
                    className="w-auto"
                  >
                    {exportStates.backup.loading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {t('settings.backingUp', { defaultValue: 'Backing up...' })}
                      </>
                    ) : exportStates.backup.success ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {t('settings.backedUp', { defaultValue: 'Backed Up' })}
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-2" />
                        {t('settings.backup', { defaultValue: 'Backup' })}
                      </>
                    )}
                      </Button>
                    </div>
                  {exportStates.backup.error && (
                    <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
                      {exportStates.backup.error}
                  </div>
                  )}
                {dataStats.lastBackup && (
                    <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-xs">
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1.5" />
                        <span>
                          {t('settings.lastBackup', { defaultValue: 'Last backup' })} {new Date(dataStats.lastBackup).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </div>
        )

      case 'security':
        return (
          <>
            <Card className="max-[1149px]:border">
              <CardHeader className="max-[1149px]:p-4">
                <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                  <Shield className="h-5 w-5" />
                  <span>{t('settings.securitySettings')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 max-[1149px]:p-4 max-[1149px]:space-y-3">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium">{t('settings.twoFactor')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.twoFactorDesc')}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {twoFactorEnabled && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowReset2FAModal(true)}
                          disabled={isResetting2FA}
                          className="text-xs"
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          {t('settings.reset2FA')}
                        </Button>
                      )}
                      <Switch checked={twoFactorEnabled} onCheckedChange={(checked) => handleToggle2FA(checked)} />
                    </div>
                  </div>
                  {twoFactorEnabled && (
                    <div className="space-y-3">
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-green-900 dark:text-green-100">
                              ✓ {t('settings.authenticatorApp')}
                            </p>
                            <p className="text-xs text-green-700 dark:text-green-300 mt-0.5">
                              {t('settings.defaultVerification')}
                            </p>
                          </div>
                          <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
                            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-muted rounded-lg border border-border">
                        <div className="flex-1">
                          <p className="font-medium text-sm mb-1">{t('settings.emailOTP', 'Email OTP')}</p>
                          <p className="text-xs text-muted-foreground">
                            {t('settings.emailOTPDesc', 'Receive codes via email as an alternative method')}
                          </p>
                        </div>
                        <Switch checked={email2FAEnabled} onCheckedChange={handleToggleEmail2FA} />
                      </div>
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          <strong>{t('settings.lostAccess')}:</strong> {t('settings.reset2FADesc')}
                        </p>
                      </div>
                      {unusedBackupCodesCount > 0 && (
                        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                          <p className="text-xs text-green-700 dark:text-green-300">
                            <strong>{t('settings.backupCodes')}</strong> {t('settings.backupCodesRemaining', { count: unusedBackupCodesCount, plural: unusedBackupCodesCount !== 1 ? 's' : '' })}.
                            {t('settings.backupCodesUse')}
                          </p>
                        </div>
                      )}
                      {unusedBackupCodesCount === 0 && (
                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                          <p className="text-xs text-yellow-700 dark:text-yellow-300">
                            <strong>{t('common.warning')}:</strong> {t('settings.noBackupCodes')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{t('settings.sessionTimeout')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.sessionDescription')}
                      </p>
                    </div>
                    <select
                      className="rounded border border-input bg-background px-3 py-1 text-sm"
                      value={String(sessionTimeout)}
                      onChange={(e) => handleSessionTimeoutChange(parseInt(e.target.value, 10))}
                      disabled={isSavingSessionTimeout}
                    >
                      <option value={240}>4 {t('settings.hours')}</option>
                      <option value={480}>8 {t('settings.hours')}</option>
                      <option value={1440}>1 {t('settings.day')}</option>
                      <option value={2880}>2 {t('settings.days')}</option>
                      <option value={10080}>1 {t('settings.week')}</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{t('settings.changePassword')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.changePasswordDesc')}
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => setShowChangePasswordModal(true)}>
                      {t('settings.change')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {showQRModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-lg">
                  <h3 className="text-lg font-semibold mb-2 dark:text-white">{t('settings.twoFactorSetup')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    {t('settings.openAuthenticatorApp')}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    {t('settings.scanQRCode')}
                  </p>
                  <div className="flex justify-center mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <img
                      src={qrCodeData}
                      alt="QR Code for 2FA setup"
                      className="w-48 h-48 border-2 border-gray-300 dark:border-gray-600 rounded"
                    />
                  </div>
                  <div className="space-y-2 mb-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                      {t('settings.scanQRInstruction')}
                    </p>
                    {isQRCodeReset && (
                      <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
                        <p className="text-xs text-yellow-700 dark:text-yellow-300">
                          <strong>{t('settings.important')}:</strong> {t('settings.newQRCodeWarning')}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        setShowQRModal(false);
                        setQrCodeData('');
                        setIsQRCodeReset(false);
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      {t('settings.doThisLater')}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowQRModal(false);
                        setQrCodeData('');
                        setIsQRCodeReset(false);
                      }}
                      className="flex-1"
                    >
                      {t('settings.done')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Reset 2FA Confirmation Modal */}
            <Modal
              isOpen={showReset2FAModal}
              onClose={() => setShowReset2FAModal(false)}
              title={t('settings.reset2FA')}
              className="max-w-md"
            >
              <div className="space-y-4">
                {/* Warning Icon */}
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-yellow-100 dark:bg-yellow-900/20 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                </div>

                {/* Warning Message */}
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t('settings.reset2FAConfirmTitle') || 'Reset 2FA QR Code?'}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t('settings.reset2FAConfirm')}
                  </p>
                </div>

                {/* Warning Box */}
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    {t('settings.reset2FAWarning')}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowReset2FAModal(false)}
                    disabled={isResetting2FA}
                  >
                    {t('settings.cancel')}
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleReset2FA}
                    disabled={isResetting2FA}
                  >
                    {isResetting2FA ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {t('settings.resetting')}
                      </>
                    ) : (
                      t('settings.reset2FA')
                    )}
                  </Button>
                </div>
              </div>
            </Modal>

            {/* Backup Codes Modal */}
            <Modal
              isOpen={showBackupCodesModal}
              onClose={() => {
                setShowBackupCodesModal(false)
                setBackupCodes([])
              }}
              title={t('settings.backupRecoveryCodes')}
              className="max-w-2xl"
            >
              <div className="space-y-4">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-yellow-800 dark:text-yellow-400 mb-2">{t('settings.saveTheseCodes')}</h3>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        {t('settings.backupCodesDescription')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-muted rounded-lg p-4 border-2 border-dashed">
                  <div className="grid grid-cols-2 gap-3">
                    {backupCodes.map((code, index) => (
                      <div
                        key={index}
                        className="p-3 bg-background rounded border font-mono text-center text-sm font-semibold tracking-wider"
                      >
                        {code}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    <strong>{t('settings.howToUseBackupCodes')}</strong> {t('settings.howToUseBackupCodesDesc')}
                  </p>
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={() => {
                      setShowBackupCodesModal(false)
                      setBackupCodes([])
                    }}
                  >
                    I've Saved These Codes
                  </Button>
                </div>
              </div>
            </Modal>

            {/* Change Password Modal */}
            <Modal
              isOpen={showChangePasswordModal}
              onClose={() => {
                setShowChangePasswordModal(false)
                setPasswordForm({
                  current_password: '',
                  new_password: '',
                  confirm_password: ''
                })
                setChangePasswordError('')
                setChangePasswordStatus('idle')
              }}
              title={t('settings.changePassword')}
            >
              <form onSubmit={handleChangePassword} className="space-y-4">
                {changePasswordStatus === 'success' && (
                  <div className="flex items-center space-x-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm text-green-700 dark:text-green-300">
                      {t('settings.passwordChangedSuccess')}
                    </span>
                  </div>
                )}

                {changePasswordError && (
                  <div className="flex items-center space-x-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800">
                    <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    <span className="text-sm text-red-700 dark:text-red-300">
                      {changePasswordError}
                    </span>
                  </div>
                )}

                <div className="space-y-2">
                  <label htmlFor="current_password" className="text-sm font-medium">
                    {t('settings.currentPassword')}
                  </label>
                  <Input
                    id="current_password"
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    placeholder={t('settings.currentPasswordPlaceholder')}
                    required
                    disabled={isChangingPassword}
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="new_password" className="text-sm font-medium">
                    {t('settings.newPassword')}
                  </label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    placeholder={t('settings.newPasswordPlaceholder')}
                    required
                    disabled={isChangingPassword}
                    minLength={8}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('settings.passwordMinLengthDescription')}
                  </p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="confirm_password" className="text-sm font-medium">
                    {t('settings.confirmPassword')}
                  </label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    placeholder={t('settings.confirmPasswordPlaceholder')}
                    required
                    disabled={isChangingPassword}
                    minLength={8}
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowChangePasswordModal(false)
                      setPasswordForm({
                        current_password: '',
                        new_password: '',
                        confirm_password: ''
                      })
                      setChangePasswordError('')
                      setChangePasswordStatus('idle')
                    }}
                    disabled={isChangingPassword}
                    className="flex-1"
                  >
                    {t('settings.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={isChangingPassword || changePasswordStatus === 'success'}
                    className="flex-1"
                  >
                    {isChangingPassword ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        {t('settings.changing')}
                      </>
                    ) : changePasswordStatus === 'success' ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {t('settings.changed')}
                      </>
                    ) : (
                      t('settings.changePassword')
                    )}
                  </Button>
                </div>
              </form>
            </Modal>
          </>
        )

      case 'system':
        return (
          <div className="space-y-6 max-[1149px]:space-y-4">
            <Card className="max-[1149px]:border">
              <CardHeader className="max-[1149px]:p-4">
                <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                  <Database className="h-5 w-5" />
                  <span>{t('settings.systemInformation')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('settings.version')}</span>
                    <span>{t('settings.versionNumber')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('settings.lastUpdated')}</span>
                    <span>{t('settings.lastUpdatedDate')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('settings.database')}</span>
                    <span>{t('settings.databaseType')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('settings.environment')}</span>
                    <span>{t('settings.environmentType')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>


          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="space-y-6 max-[1149px]:space-y-4 max-[1149px]:px-4 max-[1149px]:py-4">
      <PageHeader title={t('settings.title')} />

      <div className="flex flex-col lg:flex-row gap-6 max-[1149px]:gap-4">
        {/* Sidebar Navigation */}
        <div className="lg:w-64 lg:flex-shrink-0 space-y-1 max-[1149px]:w-auto max-[1149px]:space-y-0 max-[1149px]:overflow-x-auto max-[1149px]:pb-2">
          <div className="flex lg:flex-col gap-1 max-[1149px]:flex-row max-[1149px]:gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center justify-start gap-3 px-3 py-2 text-left text-sm rounded-lg transition-colors max-[1149px]:whitespace-nowrap max-[1149px]:flex-shrink-0 max-[1149px]:w-auto max-[1149px]:gap-2 max-[1149px]:px-2.5 max-[1149px]:py-1.5 ${activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                    }`}
                >
                  <Icon className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5 flex-shrink-0" />
                  <span className="min-w-0 flex-1 text-left leading-snug max-[1149px]:flex-none max-[1149px]:text-xs">
                    {tab.label}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="min-w-0 flex-1 max-[1149px]:w-full">
          {renderTabContent()}
        </div>
      </div>

    </div>
  )
}

function AIConfigSection() {
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [testing, setTesting] = React.useState(false)
  const [statusMsg, setStatusMsg] = React.useState<'idle' | 'success' | 'error'>('idle')
  const [lastAction, setLastAction] = React.useState<'save' | 'test' | null>(null)
  // State to hold all configurations loaded from backend
  const [configs, setConfigs] = React.useState<any[]>([])

  // Current active form state (for the selected provider)
  const [form, setForm] = React.useState({
    provider: 'ollama',
    is_active: false,
    ollama_host: '', // legacy mapping for UI
    openrouter_host: '', // legacy mapping for UI
    model: '',
    model_ocr: '',
    openrouter_model: '', // legacy mapping
    openrouter_model_ocr: '', // legacy mapping
    token: ''
  })

  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await aiAssistantApi.getConfig()
        const data = res.data
        // data should be an array of configs
        if (Array.isArray(data)) {
          setConfigs(data)
          // Initialize form with the first provider or default 'ollama'
          // Or preferably, prioritize the one that is_active=true
          let initialConfig = data.find((c: any) => c.is_active)
          if (!initialConfig) {
            initialConfig = data.find((c: any) => c.provider === 'ollama') || data[0]
          }

          if (initialConfig) {
            updateFormFromConfig(initialConfig)
          }
        }
      } catch (e) {
        // ignore
      } finally {
        setLoading(false)
      }
    }
    load()
  }, []) // Load once

  const updateFormFromConfig = (config: any) => {
    // Map generic host to specific host fields for UI if needed
    setForm({
      provider: config.provider,
      is_active: config.is_active,
      ollama_host: config.provider === 'ollama' ? (config.host || '') : '',
      openrouter_host: config.provider === 'openrouter' ? (config.host || '') : '',
      model: config.provider === 'ollama' ? (config.model || '') : '',
      model_ocr: config.provider === 'ollama' ? (config.model_ocr || '') : '',
      openrouter_model: config.provider === 'openrouter' ? (config.model || '') : '',
      openrouter_model_ocr: config.provider === 'openrouter' ? (config.model_ocr || '') : '',
      token: ''
    })
  }

  // When provider selection changes, switch the form data
  const handleProviderChange = (newProvider: string) => {
    // First, save current form state back to configs array in memory (so we don't lose unsaved edits when switching)
    // Note: we need to map back from UI fields to generic 'host'
    const currentHost = form.provider === 'ollama' ? form.ollama_host : form.openrouter_host
    const currentModel = form.provider === 'ollama' ? form.model : form.openrouter_model
    const currentModelOcr = form.provider === 'ollama' ? form.model_ocr : form.openrouter_model_ocr
    const currentToken = form.token

    const updatedConfigs = configs.map(c =>
      c.provider === form.provider
        ? { ...c, ...form, host: currentHost, model: currentModel, model_ocr: currentModelOcr, token: currentToken }
        : c
    )
    setConfigs(updatedConfigs)

    // Find the config for the new provider
    let config = updatedConfigs.find(c => c.provider === newProvider)
    if (!config) {
      // If not found (e.g. not yet created in backend), create a default structure with auto-filled values
      if (newProvider === 'ollama') {
        config = {
          provider: 'ollama',
          is_active: false,
          host: 'https://ollama.com/',
          model: 'qwen3-coder:480b-cloud',
          model_ocr: 'devstral-small-2:24b-cloud',
          token: currentToken || ''
        }
      } else if (newProvider === 'openrouter') {
        config = {
          provider: 'openrouter',
          is_active: false,
          host: 'https://openrouter.ai/api/v1',
          model: 'google/gemini-2.5-flash-lite',
          model_ocr: 'google/gemini-2.5-flash-lite',
          token: currentToken || ''
        }
      } else {
        config = {
          provider: newProvider,
          is_active: false,
          host: '',
          model: '',
          model_ocr: '',
          token: currentToken || ''
        }
      }
      setConfigs([...updatedConfigs, config])
    } else {
      // If config exists but fields are empty, auto-fill with defaults
      if (newProvider === 'ollama' && (!config.model || !config.host)) {
        config = {
          ...config,
          host: config.host || 'https://ollama.com/',
          model: config.model || 'qwen3-coder:480b-cloud',
          model_ocr: config.model_ocr || 'devstral-small-2:24b-cloud'
        }
      } else if (newProvider === 'openrouter' && (!config.model || !config.host)) {
        config = {
          ...config,
          host: config.host || 'https://openrouter.ai/api/v1',
          model: config.model || 'google/gemini-2.5-flash-lite',
          model_ocr: config.model_ocr || 'google/gemini-2.5-flash-lite'
        }
      }
    }
    updateFormFromConfig(config)
  }

  const handleSave = async () => {
    setSaving(true)
    setStatusMsg('idle')
    try {
      // Map back to generic payload
      const currentHost = form.provider === 'ollama' ? form.ollama_host : form.openrouter_host
      const currentModel = form.provider === 'ollama' ? form.model : form.openrouter_model
      const currentModelOcr = form.provider === 'ollama' ? form.model_ocr : form.openrouter_model_ocr

      const payload: Record<string, any> = {
        provider: form.provider,
        is_active: true,
        host: currentHost,
        model: currentModel,
        model_ocr: currentModelOcr,
      }
      if (form.token.trim()) {
        payload.token = form.token.trim()
      }

      await aiAssistantApi.updateConfig(payload)

      // Update local state
      setForm(prev => ({ ...prev, is_active: true }))
      // Update configs cache with just saved data
      // Update configs cache: set active for current, inactive for others
      setConfigs(prev => prev.map(c =>
        c.provider === form.provider
          ? { ...c, ...payload, is_active: true }
          : { ...c, is_active: false }
      ))

      setLastAction('save')
      setStatusMsg('success')
      setTimeout(() => setStatusMsg('idle'), 2500)
    } catch (e) {
      setLastAction('save')
      setStatusMsg('error')
      setTimeout(() => setStatusMsg('idle'), 2500)
    } finally {
      setSaving(false)
    }
  }

  const testConnection = async () => {
    setTesting(true)
    setStatusMsg('idle')
    try {
      // Testing with current form data
      const currentHost = form.provider === 'ollama' ? form.ollama_host : form.openrouter_host
      const currentModel = form.provider === 'ollama' ? form.model : form.openrouter_model

      const testPayload: any = {
        provider: form.provider,
        model: currentModel,
      }
      if (form.token.trim()) {
        testPayload.token = form.token.trim()
      }
      if (form.provider === 'ollama') {
        testPayload.ollama_host = currentHost
      } else {
        testPayload.openrouter_host = currentHost
      }

      const res = await aiAssistantApi.testConfig(testPayload)
      setLastAction('test')
      setStatusMsg(res.data.status === 'available' ? 'success' : 'error')
    } catch (e: any) {
      setLastAction('test')
      setStatusMsg('error')
    } finally {
      setTesting(false)
      setTimeout(() => setStatusMsg('idle'), 2500)
    }
  }

  const { t } = useTranslation()

  if (loading) {
    return <div className="text-sm text-muted-foreground">{t('settings.aiSettings.loading')}</div>
  }

  return (
    <div className="space-y-4">
      {/* "Use OpenRouter" switch removed as per user request */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">{t('settings.aiSettings.provider')}</label>
          <select
            value={form.provider}
            onChange={(e) => handleProviderChange(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none"
          >
            <option value="ollama">Ollama</option>
            <option value="openrouter">OpenRouter</option>
          </select>
        </div>

        {form.provider === 'ollama' && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('settings.aiSettings.model')}</label>
              <Input
                value={form.model}
                onChange={(e) => setForm((p) => ({ ...p, model: e.target.value }))}
                placeholder={t('settings.aiSettings.modelPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('settings.aiSettings.modelOcr', { defaultValue: 'Model OCR/Vision' })}</label>
              <Input
                value={form.model_ocr}
                onChange={(e) => setForm((p) => ({ ...p, model_ocr: e.target.value }))}
                placeholder={t('settings.aiSettings.modelOcrPlaceholder', { defaultValue: 'devstral-small-2:24b-cloud' })}
              />
            </div>
          </>
        )}

        {form.provider === 'openrouter' && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('settings.aiSettings.openRouterModel', { defaultValue: 'OpenRouter Model' })}</label>
              <Input
                value={form.openrouter_model}
                onChange={(e) => setForm((p) => ({ ...p, openrouter_model: e.target.value }))}
                placeholder="google/gemini-2.5-flash-lite"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('settings.aiSettings.openRouterModelOcr', { defaultValue: 'OpenRouter Model OCR' })}</label>
              <Input
                value={form.openrouter_model_ocr}
                onChange={(e) => setForm((p) => ({ ...p, openrouter_model_ocr: e.target.value }))}
                placeholder="google/gemini-2.5-flash-lite"
              />
            </div>
          </>
        )}

        {/* Conditionnally show hosts based on provider */}
        {form.provider === 'ollama' && (
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-medium">{t('settings.aiSettings.ollamaHost')}</label>
            <Input
              value={form.ollama_host}
              onChange={(e) => setForm((p) => ({ ...p, ollama_host: e.target.value }))}
              placeholder={t('settings.aiSettings.ollamaHostPlaceholder')}
            />
          </div>
        )}

        {form.provider === 'openrouter' && (
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-medium">{t('settings.aiSettings.openRouterHost', { defaultValue: 'OpenRouter Host' })}</label>
            <Input
              value={form.openrouter_host}
              onChange={(e) => setForm((p) => ({ ...p, openrouter_host: e.target.value }))}
              placeholder="https://openrouter.ai/api/v1"
            />
          </div>
        )}

        <div className="space-y-2 md:col-span-2">
          <label className="text-sm font-medium">{t('settings.aiSettings.token')}</label>
          <Input
            type="password"
            value={form.token}
            onChange={(e) => setForm((p) => ({ ...p, token: e.target.value }))}
            placeholder="Leave blank to keep the current token"
            autoComplete="new-password"
          />
        </div>
      </div>

      <div className="pt-2">
        {statusMsg !== 'idle' && (
          <div
            className={`mb-2 p-3 rounded-lg text-sm ${statusMsg === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
              }`}
          >
            <div className="flex items-center justify-between">
              <span>
                {statusMsg === 'success'
                  ? (lastAction === 'test' ? t('settings.aiSettings.connectionOK') : t('settings.aiSettings.savedSuccessfully'))
                  : (lastAction === 'test' ? t('settings.aiSettings.connectionFailed') : t('settings.aiSettings.saveFailed'))}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setStatusMsg('idle')
                }}
                className="px-2 py-1 text-xs opacity-70 hover:opacity-100"
                aria-label={t('settings.aiSettings.dismiss')}
              >
                ✕
              </button>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              testConnection()
            }}
            disabled={testing}
          >
            {testing ? t('settings.aiSettings.testing') : t('settings.aiSettings.testConnection')}
          </Button>
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleSave()
            }}
            disabled={saving}
          >
            {saving ? t('settings.aiSettings.saving') : t('settings.aiSettings.save')}
          </Button>
        </div>
      </div>
    </div >
  )
}
